/* HR assistant side drawer (prototype ai.js assistantDrawer): chat via POST /ai/chat,
 * suggestion chips, Enter to send, auto-scroll, replies in the current language. */
import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useSession } from "../../app/session";
import { useAiChat, useAiFeatures } from "../../data/hooks";
import { useI18n } from "../../i18n";
import { Icon, Modal, errMsg } from "../../ui";

interface Msg { who: "ai" | "me"; text: string; err?: boolean }

export default function AssistantDrawer({ onClose }: { onClose: () => void }) {
  const { L, lang } = useI18n();
  const { user, role } = useSession();
  const chat = useAiChat();
  const cfg = useAiFeatures({ enabled: role === "owner" || role === "hr" || role === "accountant" });
  const inputId = useId();
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [q, setQ] = useState("");
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { inputRef.current?.focus({ preventScroll: true }); }, []);
  const first = user ? L(user.name, user.nameAr).split(" ")[0] : "";

  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, chat.isPending]);

  const ask = async (text: string) => {
    const m = text.trim();
    if (!m || chat.isPending) return;
    setMsgs((x) => [...x, { who: "me", text: m }]);
    setQ("");
    try {
      const r = await chat.mutateAsync({ message: m, lang });
      setMsgs((x) => [...x, { who: "ai", text: r.reply }]);
    } catch (e) {
      const msg = /credit/i.test(errMsg(e)) ? L("Your company has used all AI credits for this month.", "استنفدت منشأتك رصيد الذكاء لهذا الشهر.") : errMsg(e);
      setMsgs((x) => [...x, { who: "ai", text: msg, err: true }]);
    }
  };
  const onSubmit = (e: FormEvent) => { e.preventDefault(); void ask(q); };

  const sug = role === "employee"
    ? [L("How many leave requests are pending?", "كم طلب إجازة معلق؟"), L("Which documents expire soon?", "ما الوثائق التي تنتهي قريبًا؟"), L("How many employees do we have?", "كم عدد الموظفين؟")]
    : [L("How many employees do we have?", "كم عدد الموظفين لدينا؟"), L("What is this month's payroll total?", "ما إجمالي رواتب هذا الشهر؟"), L("Which Iqamas expire soon?", "ما الإقامات التي تنتهي قريبًا؟"), L("What is our Saudization rate?", "ما نسبة السعودة لدينا؟"), L("How many leave requests are pending?", "كم طلب إجازة معلق؟")];
  const assistant = cfg.data?.features.find((f) => f.id === "assistant");

  return (
    <Modal className="drawer" onClose={onClose} testId="assistant"
      title={<><span className="aitag"><Icon name="sparkle" className="s" />AI</span> {L("HR assistant", "المساعد الذكي")}</>}
      foot={
        <>
          <form className="chat-in" onSubmit={onSubmit} style={{ width: "100%" }}>
            <label htmlFor={inputId} className="sr">{L("Ask the assistant", "اسأل المساعد")}</label>
            <input ref={inputRef} id={inputId} className="inp" value={q} autoComplete="off" onChange={(e) => setQ(e.target.value)} placeholder={L("Ask anything… e.g. “How many employees do we have?”", "اسأل أي شيء… مثل «كم عدد الموظفين؟»")} data-testid="chat-input" />
            <button type="submit" className="btn pri" disabled={!q.trim() || chat.isPending} data-testid="chat-send"><Icon name="chevron-right" /><span className="sr">{L("Send", "إرسال")}</span></button>
          </form>
          <div className="xs muted" style={{ width: "100%" }} data-testid="chat-meta">
            {assistant ? <>{L("Model ", "النموذج ")}<span className="num">{assistant.model}</span> · </> : null}
            {cfg.data ? L(`${cfg.data.used} of ${cfg.data.limit} credits used this month`, `استُخدم ${cfg.data.used} من ${cfg.data.limit} رصيد`) : L("Answers use only data your role can see", "الإجابات تستخدم البيانات المسموحة لدورك فقط")}
          </div>
        </>
      }>
      <div className="chat">
        <div className="chat-log" id="chat-log" ref={logRef} aria-live="polite" data-testid="chat-log">
          <div className="msg ai"><span className="aiav"><Icon name="sparkle" className="s" /></span><div>{L(`Hi${first ? " " + first : ""}, I'm your HR assistant. Ask me about the Labor Law, your people or your payroll — in Arabic or English.`, `مرحبًا${first ? " " + first : ""}، أنا مساعدك للموارد البشرية. اسألني عن نظام العمل أو الموظفين أو الرواتب بالعربية أو الإنجليزية.`)}</div></div>
          {msgs.map((m, i) => m.who === "ai"
            ? <div key={i} className={"msg ai" + (m.err ? " err" : "")} data-testid="msg-ai"><span className="aiav"><Icon name={m.err ? "alert" : "sparkle"} className="s" /></span><div>{m.text}{!m.err && <div className="aisrc"><Icon name="file" className="s" />{L("Answer uses only data your role can see", "الإجابة تستخدم البيانات المسموحة لدورك فقط")}</div>}</div></div>
            : <div key={i} className="msg me" data-testid="msg-me"><div>{m.text}</div></div>)}
          {chat.isPending && <div className="msg ai" data-testid="chat-thinking"><span className="aiav"><Icon name="sparkle" className="s" /></span><div><div className="aithink"><span className="aidots"><i /><i /><i /></span>{L("Thinking…", "يفكر…")}</div></div></div>}
        </div>
        <div className="chips2">
          {sug.map((x) => <button key={x} type="button" className="chip2" onClick={() => ask(x)} disabled={chat.isPending} data-testid="chat-chip">{x}</button>)}
        </div>
      </div>
    </Modal>
  );
}
